import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Heart, Star, MapPin, Quote, MessageSquare, Loader2, ChevronDown } from 'lucide-react';
import { Hero } from '@/components/features/Hero';
import { ServiceCard } from '@/components/features/ServiceCard';
import { ContactCTA } from '@/components/features/ContactCTA';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SERVICES } from '@/lib/constants';

interface Testimonial {
  id: string;
  name: string;
  location: string | null;
  service: string | null;
  text: string;
  rating: number;
  featured: boolean;
}

const whyChooseUs = [
  {
    icon: <Shield className="h-6 w-6" />,
    title: 'Trusted & Reliable',
    description: 'Dependable service you can count on, every time.',
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Flexible Scheduling',
    description: 'Services tailored to fit your busy lifestyle.',
  },
  {
    icon: <Heart className="h-6 w-6" />,
    title: 'Personal Touch',
    description: 'One-on-one service with attention to detail.',
  },
  {
    icon: <MapPin className="h-6 w-6" />,
    title: 'Locally Owned',
    description: 'Proudly serving the Tasmanian community.',
  },
];

const serviceLabels: Record<string, string> = {
  gardening: 'Gardening',
  cleaning: 'Cleaning',
  taxidermy: 'Taxidermy',
  general: 'General',
};

// Service areas within ~100km of Lachlan, TAS 7140
const serviceAreas = [
  'Hobart',
  'New Norfolk',
  'Glenorchy',
  'Brighton',
  'Bridgewater',
  'Derwent Valley',
];

const stats = [
  { value: '3', label: 'Service Categories' },
  { value: 'All TAS', label: 'Service Area' },
  { value: '24-48h', label: 'Response Time' },
  { value: '100%', label: 'Satisfaction Goal' },
];

// FAQ data for SEO
const faqs = [
  {
    question: 'What areas do you service in Tasmania?',
    answer: 'I\'m based in Lachlan in the Derwent Valley and service all of Tasmania. Local areas like Hobart, New Norfolk, Glenorchy, and the Derwent Valley are serviced without travel fees. Further locations are available by arrangement with a small travel fee.',
  },
  {
    question: 'How do I get a quote for your services?',
    answer: 'Simply fill out the contact form on this website or email me directly at coco@cloud9tasmania.com. I typically respond within 24-48 hours with a free, no-obligation quote tailored to your needs.',
  },
  {
    question: 'Do you offer regular gardening and cleaning services?',
    answer: 'Yes! I offer both one-off services and regular maintenance schedules. Whether you need weekly lawn mowing, fortnightly cleaning, or seasonal garden care, I can create a schedule that works for you.',
  },
  {
    question: 'What\'s included in your end of lease cleaning?',
    answer: 'End of lease (bond) cleaning includes a comprehensive deep clean of the entire property: all rooms, kitchen appliances, bathrooms, windows, floors, and more. I aim to help you get your full bond back.',
  },
  {
    question: 'What types of taxidermy do you offer?',
    answer: 'I specialize in bird and small mammal taxidermy, including native Tasmanian species. Services include preservation, mounting, restoration of existing pieces, and custom display work. Each project is handled with care to capture the natural beauty of the specimen.',
  },
];

/**
 * Home page component
 */
export function Home() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);

  // Fetch testimonials from API
  useEffect(() => {
    async function fetchTestimonials() {
      try {
        const response = await fetch('/api/testimonials');
        const data = await response.json();
        setTestimonials(data.testimonials || []);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      } finally {
        setIsLoadingTestimonials(false);
      }
    }
    fetchTestimonials();
  }, []);

  return (
    <>
      {/* Hero Section */}
      <Hero />

      {/* Services Section */}
      <section className="section-padding" aria-labelledby="services-heading">
        <div className="container-custom">
          {/* Section header */}
          <div className="mb-12 text-center">
            <span className="category-label">What I Offer</span>
            <h2 id="services-heading" className="text-3xl font-bold md:text-4xl">
              Professional Services
            </h2>
            <p className="mt-4 text-muted-foreground">
              Three distinct service categories to meet your needs
            </p>
          </div>

          {/* Service cards grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Object.values(SERVICES).map((service) => (
              <ServiceCard
                key={service.id}
                id={service.id}
                title={service.title}
                description={service.description}
                href={service.href}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-card" aria-labelledby="why-choose-heading">
        <div className="container-custom">
          <div className="mb-12 text-center">
            <span className="category-label">Why Cloud9</span>
            <h2 id="why-choose-heading" className="text-3xl font-bold md:text-4xl">
              Why Choose Me
            </h2>
            <p className="mt-4 text-muted-foreground">
              Quality service with a personal touch
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="card-hover text-center">
                <CardContent className="pt-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-primary text-primary-foreground">
        <div className="container-custom">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold md:text-4xl">{stat.value}</div>
                <div className="mt-1 text-sm opacity-80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding" aria-labelledby="testimonials-heading">
        <div className="container-custom">
          <div className="mb-12 text-center">
            <span className="category-label">Testimonials</span>
            <h2 id="testimonials-heading" className="text-3xl font-bold md:text-4xl">
              What Customers Say
            </h2>
            <p className="mt-4 text-muted-foreground">
              {testimonials.length > 0 ? "Don't just take my word for it" : "Your feedback means the world to me"}
            </p>
          </div>

          {isLoadingTestimonials ? (
            // Loading state
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : testimonials.length === 0 ? (
            // Empty state - Be our first customer
            <Card className="mx-auto max-w-xl border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MessageSquare className="h-8 w-8" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold">Be Our First Review!</h3>
                <p className="mt-2 max-w-sm text-muted-foreground">
                  I'm just getting started and would love to hear about your experience.
                  Your testimonial could be featured right here!
                </p>
                <Button asChild className="mt-6 gap-2">
                  <Link to="/contact">
                    Get in Touch
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            // Testimonials grid
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="card-hover relative">
                  <CardContent className="pt-6">
                    {/* Quote icon */}
                    <Quote className="absolute right-4 top-4 h-8 w-8 text-primary/10" aria-hidden="true" />

                    {/* Stars */}
                    <div className="mb-4 flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
                      ))}
                    </div>

                    {/* Testimonial text */}
                    <p className="text-muted-foreground italic">
                      "{testimonial.text}"
                    </p>

                    {/* Author */}
                    <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                      <div>
                        <p className="font-semibold">{testimonial.name}</p>
                        {testimonial.location && (
                          <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                        )}
                      </div>
                      {testimonial.service && (
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                          {serviceLabels[testimonial.service] || testimonial.service}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="section-padding bg-card" aria-labelledby="areas-heading">
        <div className="container-custom">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            {/* Content */}
            <div>
              <span className="category-label">Service Areas</span>
              <h2 id="areas-heading" className="text-3xl font-bold md:text-4xl">
                Serving All of Tasmania
              </h2>
              <p className="mt-4 text-muted-foreground">
                Based in Lachlan in the Derwent Valley. Areas listed below are generally
                serviced without travel fees. Further locations available - contact me
                to discuss requirements.
              </p>

              {/* Areas grid */}
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {serviceAreas.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm"
                  >
                    <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                    {area}
                  </div>
                ))}
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                Don't see your area? No problem! I service all of Tasmania - areas beyond
                the above may incur a small travel fee. Get in touch to discuss.
              </p>

              <Button asChild className="mt-6 gap-2">
                <Link to="/contact">
                  Get in Touch
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>

            {/* Map placeholder / Visual */}
            <Card className="overflow-hidden border-0 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="flex aspect-square items-center justify-center p-8 lg:aspect-auto lg:min-h-[400px]">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MapPin className="h-10 w-10" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold">Tasmania-Wide Service</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    No travel fees for local areas<br />
                    Further locations by arrangement
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding" aria-labelledby="faq-heading">
        <div className="container-custom">
          <div className="mb-12 text-center">
            <span className="category-label">FAQ</span>
            <h2 id="faq-heading" className="text-3xl font-bold md:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-muted-foreground">
              Got questions? I've got answers
            </p>
          </div>

          <div className="mx-auto max-w-3xl space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-lg border border-border bg-card"
              >
                <summary className="flex cursor-pointer items-center justify-between p-4 font-medium hover:bg-muted/50 transition-colors [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-4 pb-4 text-muted-foreground">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
